import NavLinkAdapter from '@fuse/core/NavLinkAdapter';
import { styled, alpha } from '@mui/material/styles';
import Icon from '@mui/material/Icon';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { useMemo } from 'react';
import FuseNavBadge from '../../FuseNavBadge';
import { FEATURED_COLOR } from '../../featured';

const NAV_ICON_BOX_CLASSES =
  'fuse-list-item-icon shrink-0 flex items-center justify-center vtwo:size-6.5 vtwo:mr-3';

const featuredIconStyle = {
  backgroundColor: FEATURED_COLOR,
};

const Root = styled(ListItem, {
  shouldForwardProp: (prop) => prop !== 'featured' && prop !== 'itempadding',
})(({ theme, itempadding, featured, ...props }) => ({
  height: 36,
  width: '100%',
  borderRadius: '6px',
  margin: '0 0 4px 0',
  paddingRight: 12,
  paddingLeft: itempadding,
  color: alpha(theme.palette.text.primary, 1),
  cursor: 'pointer',
  textDecoration: 'none!important',
  '&:hover': {
    color: theme.palette.text.primary,
  },
  '&.active': {
    color: theme.palette.text.primary,
    backgroundColor: featured ? FEATURED_COLOR : '#4DB181',
    pointerEvents: 'none',
    transition: 'border-radius .15s cubic-bezier(0.4,0.0,0.2,1)',
    '& > .fuse-list-item-text-primary': {
      color: 'inherit',
    },
    '& > .fuse-list-item-icon': {
      color: 'inherit',
    },
  },
  '& >.fuse-list-item-icon': {
    marginRight: 12,
    color: 'inherit',
  },
  '& > .fuse-list-item-text': {},
}));

function FuseNavVerticalItem(props) {
  const { item, nestedLevel, onItemClick } = props;

  const itempadding = nestedLevel > 0 ? 28 + nestedLevel * 16 : 12;
  return useMemo(
    () => (
      <>
        {item.display !== false && (
          <Root
            button
            component={NavLinkAdapter}
            to={item.url}
            activeClassName="active"
            className="fuse-list-item"
            onClick={() => onItemClick && onItemClick(item)}
            end={item.end}
            itempadding={itempadding}
            featured={!!item.featured}
            role="button"
          >
            {(item.icon || item.img) && <NavItemIcon item={item} />}

            <ListItemText
              className="fuse-list-item-text"
              primary={item.title}
              classes={{
                primary: clsx('fuse-list-item-text-primary', [item.bold ? 'font-bold' : null]),
              }}
            />
            {item.badge && <FuseNavBadge badge={item.badge} />}
          </Root>
        )}
      </>
    ),
    [item, itempadding, onItemClick]
  );
}

FuseNavVerticalItem.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string,
    icon: PropTypes.string,
    url: PropTypes.string,
  }),
};

function NavItemIcon({ item }) {
  const featured = !!item.featured;

  return (
    <span
      className={clsx(NAV_ICON_BOX_CLASSES, featured && 'vtwo:rounded-md')}
      style={featured ? featuredIconStyle : undefined}
    >
      {item.img ? (
        <img className="vtwo:size-5" src={item.img} alt={item.title} />
      ) : (
        <Icon
          className={clsx('vtwo:text-[20px]!', item.iconClass || (featured ? 'text-white' : null))}
        >
          {item.icon}
        </Icon>
      )}
    </span>
  );
}

const NavVerticalItem = FuseNavVerticalItem;

export default NavVerticalItem;
